import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpRes } from '../../../models/common/http.model';
import { BulkComponentSummaryPayload } from '../../../models/common/events_payloads';
import { rating_urls } from '../Constants/api-constants';
import { rnr_end_points } from '../Constants/api-constants';
import { SKIP_GLOBAL_LOADER } from '../loading/skip-global-loader.context';
export interface CategoryLevel {
  level: string;
  description: string;
  id: number;
  is_deleted: boolean;
}

export interface Category {
  id: number;
  participant_type_id: number;
  published: boolean;
  category_level_id: number | null;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  updated_by: string | null;
  category_description: string;
  is_active: boolean;
  admin_only: boolean;
  gender_id: number | null;
  created_by: string | null;
  is_on_hold?: boolean;
  hold_reason?: string | null;
}

export interface CategoryResponse {
  code: number;
  msg: string;
  data: Category[];
}

export interface StandingParent {
  name: string;
  country: string | null;
  role_id: number;
  role_name: string;
  country_flag: string | null;
  user_role_id: number;
}

export interface Standing {
  gender_id: number | null;
  rank: number;
  participant_id: number;
  participant_name: string;
  participant_type_id: number;
  participant_user_role_ids: string;
  country_code: string | null;
  club_name: string | null;
  total_points: number;
  parents?: StandingParent[];
  event_breakdown: {
    event_id: number;
    event_name: string;
    total_points_from_event: number;
    stage_points: { [key: string]: number };
    allocation_date: string;
    expiry_date: string;
  }[];
  movement_indicator: string;
  has_manual_adjustment: boolean;
  is_paused: boolean;
  penalty_points: number | null;
  user_info: {
    user_role_id: number;
    role_id: number;
    role_name: string;
    gender: string;
    citizen_type: string;
    name: string;
    email: string;
    dob: string;
    rating: number;
    country: string | null;
    license_id: string | null;
    player_profile: string | null;
    parents: {
      parent_user_role_id: number;
      parent_role_name: string;
      parent_role_id: number;
      parent_role_field_name: string;
      parent_role_field_value: string;
      parent_user_role_fields_data: {
        Website?: string;
        Email?: string;
        'Office Address'?: string;
        Country?: string;
        countryFlag?: string;
        countryAbbr?: string;
        'Full Name'?: string;
        Abbreviation?: string;
      };
    }[];
  };
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
  observe: 'response' as 'response',
};
const httpAuthOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
  observe: 'response' as 'response',
};

export interface RankingsResponse {
  items: any[];
  standings: Standing[];
  metadata: {
    calculation_timestamp: string;
    filters_applied: {
      season_id: number;
      category_id: number;
      urs_data_for_entity_map_json_str: any[];
    };
    rnr_tenant_context: string;
    total_count: number;
    current_page: number;
    per_page: number;
    total_pages: number;
  };
  last_updated_at?: string;
}

export interface User {
  user_role_id: number;
  role_id: number;
  role_name: string;
  parents: any[];
  gender: string;
  citizen_type: string;
  name: string;
  email: string;
  dob: string;
  rating: number;
  country: string | null;
  license_id: string;
}

export interface UsersResponse {
  code: number;
  msg: string;
  data: User[];
  count: number;
}

export interface UsersPageResult {
  users: User[];
  count: number;
}

export const CLUBS_PAGE_SIZE = 50;

export interface GetUsersParams {
  roleId: number;
  searchValue?: string;
  pageNum?: number;
  perPage?: number;
  /** Avoid full-page loader for background filter list pagination/search. */
  skipGlobalLoader?: boolean;
}

export interface Season {
  season_name: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  is_active: boolean;
  id: string;
  status: string;
}

export interface SeasonsResponse {
  seasons: Season[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    limit: number;
  };
}

export interface TimelineItem {
  date: string;
  type: string;
  status: string;
  ranking_list_id: string | null;
  week_label?: string;
  month_label?: string;
}

export interface TimelineResponse {
  configure: string;
  by_date: TimelineItem[];
  by_week: TimelineItem[];
  by_month: TimelineItem[];
}

/** Per-category point slice returned on each event in event-performance. */
export interface EventCategoryBreakdownItem {
  category_id: number;
  category_name: string;
  participant_type_id: number;
  participant_type_name: string;
  config_type: string | null;
  points: number;
  ranking_list_id: string | null;
  ranking_list_ids?: string[];
  category_level?: string | null;
  category_level_name?: string | null;
}

export interface EventPerformance {
  event_id: number | null;
  event_name: string;
  event_type?: string | null;
  points: number;
  total_points_from_event?: number;
  allocation_date: string;
  expiry_date: string | null;
  is_highlighted: boolean | null;
  is_external?: boolean;
  is_carry_forward_placeholder?: boolean;
  is_merged_team_event?: boolean;
  prioritized_included?: boolean;
  prioritized_type?: string | null;
  ranking_list_ids?: string[];
  ranking_list_category_map?: Record<
    string,
    {
      category_id: number;
      category_name: string;
      category_description?: string;
      calculation_basis?: string | null;
    }
  >;
  ranking_list_basis_map?: Record<string, string | null>;
  source_category_id?: number;
  category_id?: number;
  category_name?: string;
  category_description?: string;
  category_breakdown?: EventCategoryBreakdownItem[];
  merged_events?: EventPerformance[];
}

export interface EventPerformanceResponse {
  participant_id: number;
  participant_name: string;
  is_paused: boolean;
  season_id: number;
  category_id: number;
  event_performances: EventPerformance[];
}

export interface CombinedEventBreakdownMatch {
  match_id: number;
  match_date: string;
  match_status: string | null;
  opponent_name: string | null;
  opponent_rating: number | null;
  score: string | null;
  rating_before: number | null;
  rating_after: number | null;
  rating_exchange: number | null;
  victory_points: number | null;
  bonus_points: number | null;
}

export interface CombinedEventBreakdownCategory {
  category_id: number;
  category_name: string;
  victory_points: number | null;
  bonus_points: number | null;
  position_points: number | null;
  ranking_total: number | null;
  rating_initial: number | null;
  rating_delta: number | null;
  rating_final: number | null;
  matches: CombinedEventBreakdownMatch[];
  total_matches: number | null;
  wins: number | null;
  losses: number | null;
  ties: number | null;
}

export interface CombinedEventBreakdownEvent {
  event_id: number;
  event_name: string;
  event_date: string;
  rating_initial: number | null;
  rating_delta: number | null;
  rating_final: number | null;
  bonus_points: number | null;
  total_points: number | null;
  is_external?: boolean;
  priority_tag?: 'top' | 'bottom' | null;
  is_counted?: boolean;
  categories: CombinedEventBreakdownCategory[];
}

/** Per-category point breakdown for a single tournament in the rating leaderboard. */
export interface RatingCategoryBreakdown {
  category_id: number;
  category_name: string;
  victory_points: number;
  bonus_points: number;
  penalty_points: number;
  manual_vp_adjustment: number;
  manual_bp_adjustment: number;
  tournament_weight: number;
  total_points: number;
  last_updated_at: string;
}

/** Response from GET ott/v1/rating/player/{player_id}/breakdown/tournament/{event_id}. */
export interface RatingTournamentBreakdownResponse {
  player_id: number;
  player_name: string;
  event_id: number;
  event_name: string;
  category_breakdowns: RatingCategoryBreakdown[];
}

/** Single match row in the rating match-level breakdown response. */
export interface RatingMatchLevelMatch {
  match_id: number;
  match_status: string;
  opponent_name: string;
  match_date: string | null;
  score: string;
  opponent_rating: number;
  vp: number;
  bp: number;
  is_held?: boolean;
  hold_reason?: string | null;
}

/** Rating group with matches and totals in the match-level breakdown response. */
export interface RatingMatchLevelGroup {
  initial_rating: number;
  total_vp: number;
  total_bp: number;
  matches: RatingMatchLevelMatch[];
}

/** Response from GET ott/v1/rating/player/{player_id}/breakdown/tournament/{event_id}/category/{category_id}/match-level. */
export interface RatingMatchLevelBreakdownResponse {
  player_id: number;
  category_id: number;
  event_id: number;
  tournament_name: string;
  rating_breakdown: RatingMatchLevelGroup[];
}

export interface CombinedEventBreakdownResponse {
  player_id: number;
  player_name: string | null;
  overall_rating_initial: number | null;
  overall_rating_current: number | null;
  overall_rating_change: number | null;
  overall_ranking_points: number | null;
  overall_total_points: number | null;
  events: CombinedEventBreakdownEvent[];
  metadata?: any;
}

export interface EventMatchBreakdownMatch {
  match_id: number;
  match_date: string | null;
  match_status: string | null;
  opponent_id: number | null;
  opponent_name: string | null;
  opponent_rating: number | null;
  score: string | null;
  rating_before: number | null;
  rating_after: number | null;
  rating_exchange: number | null;
}

export interface EventMatchBreakdownResponse {
  player_id: number;
  player_name: string | null;
  event_id: number;
  event_name: string;
  event_date: string | null;
  category_id: number;
  category_name: string;
  rating_initial: number | null;
  rating_final: number | null;
  rating_change: number | null;
  bonus_points: number | null;
  total_points: number | null;
  matches: EventMatchBreakdownMatch[];
  total_matches: number | null;
  wins: number | null;
  losses: number | null;
  ties: number | null;
  metadata?: any;
}

export interface ExpiredEvent {
  event_id: number;
  event_name: string;
  points_expired: number;
  allocation_date: string;
  expiry_date: string;
}

export interface ExpiredPointsResponse {
  participant_id: number;
  participant_name: string;
  expired_events: ExpiredEvent[];
}

export interface RankingListItem {
  ranking_list_id: string;
  tenant_id: string;
  season_id: number;
  category_id: number;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  approval_status: string;
  trigger_source: string;
  calculated_for_tournament_ids: string[];
  parent_category_id?: number;
}

export interface RankingListsResponse {
  items: RankingListItem[];
  total_count: number;
  current_page: number;
  per_page: number;
  total_pages: number;
}

export interface MatchBreakdown {
  match_id: number;
  match_date: string | null;
  match_status: string;
  opponent_name: string;
  match_score: string;
  match_total_points_from_this_stage: number;
  match_point_components: {
    [key: string]: {
      value: number;
      contributing_logs: {
        component_source: string;
        stage_id: string | null;
        points_component_value: number;
        multiplier_applied: string | null;
        component_details: {
          [key: string]: any;
        };
        created_at: string;
      }[];
    };
  };
}

export interface Stage {
  stage_id: number;
  stage_name: string;
  stage_total_points: number;
  matches_breakdown: MatchBreakdown[];
  stage_level_bonus_points: { [key: string]: any };
}

export interface EventBreakdown {
  event_id: number;
  event_name: string;
  stages: Stage[];
  event_wide_components: { [key: string]: any };
  event_total_for_participant: number;
  event_point_summary: { [key: string]: number };
}

export interface ComponentSummaryResponse {
  ranking_list_id: string;
  participant_id: number;
  participant_name: string;
  participant_type_id: number;
  participant_user_role_ids: string;
  events_breakdown: EventBreakdown[];
  overall_total_points_in_list: number;
}

/** Flat event-wide point totals returned by the v2 component-summary API. */
export interface ComponentSummaryV2EventWide {
  match_outcome_points: number;
  victory_points: number;
  position_points: number;
  bonus_points: number;
  penalty_points: number;
  expiry_deduction_points: number;
  total_points: number;
}

/** Match row returned by the v2 component-summary API. */
export interface ComponentSummaryV2Match {
  match_id: number;
  parent_match_id: number | null;
  opponent_name: string;
  opponent_player_id: number;
  score: string;
  result: string;
  points: number;
  breakdown: {
    submatch_win_loss_points?: number;
    victory_points?: number;
    bonus_points?: number;
    walkover_forfeit_points?: number;
    other_points?: number;
  };
}

/** Stage row returned by the v2 component-summary API. */
export interface ComponentSummaryV2Stage {
  stage_id: number;
  stage_label: string;
  stage_name: string;
  match_outcome_points: number;
  victory_points: number;
  position_points: number;
  bonus_points: number;
  penalty_points: number;
  expiry_deduction_points: number;
  tournament_weight: number | null;
  stage_total_points: number;
  matches: ComponentSummaryV2Match[];
}

/** Event row returned by the v2 component-summary API. */
export interface ComponentSummaryV2Event {
  event_id: number;
  event_name: string;
  event_total_points: number;
  stages: ComponentSummaryV2Stage[];
  event_wide: ComponentSummaryV2EventWide;
}

/** Response shape for v2 component-summary-by-event-stage endpoint. */
export interface ComponentSummaryV2Response {
  ranking_list_id: string;
  participant: {
    id: number;
    name: string;
    type_id: number;
  };
  overall_total_points: number;
  has_pending_changes: boolean;
  events: ComponentSummaryV2Event[];
}

export interface ExportRankingsPayload {
  season_id: number;
  category_id: number;
  date_from?: string;
  date_to?: string;
  event_ids?: number[];
  parents?: number[];
  filter_rank_min?: number;
  filter_rank_max?: number;
  search?: string;
  format: string;
}

export interface OttLeaderboardFilterConfig {
  enable_rating_period: boolean;
  enabled_classification_fields: string[];
}

export interface RatingFilterConfig {
  enable_gender_filter: boolean;
  enable_age_filter: boolean;
  player_association_roles: string[];
  // Optional: configs saved before the OTT leaderboard filters existed lack it.
  ott_leaderboard?: OttLeaderboardFilterConfig;
}

/** One parent-role row filter (matched by RNR against publish-time snapshots). */
export interface ParentRoleFilter {
  role_name: string;
  user_role_ids: number[];
}

/** One classification row filter; isolates by checked value, with
 *  NON_CLASSIFIED_VALUE standing for the non-classified cohort. */
export interface ClassificationFilter {
  field_name: string;
  selected_values: string[];
}

/** Wire value for the non-classified cohort in a classification filter. It is a
 *  normal selectable value (PRD v4.1): checked by default, unchecking it hides
 *  players with no value for that field. Must stay in sync with RNR's
 *  NON_CLASSIFIED_SENTINEL in src/crud/rating_list_crud.py. */
export const NON_CLASSIFIED_VALUE = 'None';

/** True for any spelling of the non-classified cohort. Tenant value_json is
 *  free text, so 'none' / 'NONE' / ' None' must all collapse onto the single
 *  sentinel row; RNR normalises identically when matching. */
export function isNonClassified(value: string): boolean {
  return (value ?? '').trim().toLowerCase() === NON_CLASSIFIED_VALUE.toLowerCase();
}

/** API values for the rating leaderboard player category filter. */
export type PlayerCategory = 'active_player' | 'inactive_player';

/** Default player-status filter — the leaderboard always shows Active players until the user picks Inactive. */
export const DEFAULT_PLAYER_CATEGORY: PlayerCategory = 'active_player';

export interface RoundingRule {
  function: 'ceiling' | 'floor' | string; // Allow string for flexibility if API changes
  decimal_places: number;
}

export interface CategoryItem {
  id: number;
  category_description: string;
  abbr: string | null;
  participant_type_description: string;
  gender: string | null;
  category_level_name: string;
}

export interface PaginatedCategoriesResponse {
  items: CategoryItem[];
  total: number;
  page: number;
  size: number;
}

export type LastPublishedDatePayload = Partial<{
  category_ids?: number | null;
  season_id?: number | string | null;
}>;

export interface WorldRankSettings {
  world_rank_enabled: boolean;
  prioritize_rank: string;
  rank_name: string;
  rank_abbreviation: string;
}

export interface LeaderboardSystemSettings {
  use_ranking: boolean;
  use_rating: boolean;
  use_ranking_and_rating: boolean;
}

export type LeaderboardMode =
  | 'Ranking'
  | 'Rating'
  | 'RankingAndRating'
  | 'None';

export function buildHttpParams(obj: Record<string, any>): HttpParams {
  const filtered: Record<string, any> = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      filtered[key] = value;
    }
  });
  return new HttpParams({ fromObject: filtered });
}
@Injectable({
  providedIn: 'root',
})
export class PointLeaderboardService {
  private baseUrl = `${environment.apiUrl}`;
  private readonly defaultSystemSettings: LeaderboardSystemSettings = {
    use_ranking: environment.use_ranking ?? false,
    use_rating: environment.use_rating ?? false,
    use_ranking_and_rating: false,
  };
  private readonly systemSettingsSubject =
    new BehaviorSubject<LeaderboardSystemSettings>(this.defaultSystemSettings);
  private readonly leaderboardModeSubject = new BehaviorSubject<LeaderboardMode>(
    this.resolveLeaderboardMode(this.defaultSystemSettings),
  );
  readonly systemSettings$ = this.systemSettingsSubject.asObservable();
  readonly leaderboardMode$ = this.leaderboardModeSubject.asObservable();

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
  ) {}

  private getHttpOptions(params?: HttpParams): {
    headers: HttpHeaders;
    params?: HttpParams;
  } {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      tenant: environment.tenant_Name,
      auth:
        'Bearer ' +
        (typeof window !== 'undefined' && localStorage.getItem('token')
          ? localStorage.getItem('token')
          : ''),
      language: 'en',
      'ngrok-skip-browser-warning': 'true',
    });
    return { headers, params };
  }

  getCategoryLevels(): Observable<CategoryLevel[]> {
    const url = `${this.baseUrl}v1/category_levels`;
    return this.http.get<CategoryLevel[]>(url, this.getHttpOptions()).pipe(
      map((response) => response.filter((level) => !level.is_deleted)),
      catchError((error) => {
        console.error('Error fetching category levels:', error);
        return of([]);
      }),
    );
  }

  getCategories(load_ranking_categories?: boolean): Observable<Category[]> {
    const url = `${this.baseUrl}ott/v1/get_categories`;
    let params = new HttpParams();
    if (load_ranking_categories !== undefined) {
      params = params.set('load_ranking_categories', load_ranking_categories);
    }
    return this.http.get<CategoryResponse>(url, this.getHttpOptions(params)).pipe(
      map((response) => {
        if (response.code === 200) {
          return response.data.filter(
            (category) =>
              !category.is_deleted && category.category_level_id !== null,
          );
        }
        return [];
      }),
      catchError((error) => {
        console.error('Error fetching categories:', error);
        return of([]);
      }),
    );
  }

  getRatingLeaderboard(payload: any): Observable<any> {
    const url = `${this.baseUrl}ott/v1/rating/leaderboard/search`;
    let params = new HttpParams().set('is_ott', true);
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
        // Add auth token if required by your application setup
      }),
      params: params,
    };
    return this.http.post<any>(url, payload, httpOptions).pipe(
      catchError((err) => {
        console.error('Error fetching rating leaderboard:', err);
        return throwError(
          () => new Error('Failed to fetch rating leaderboard'),
        );
      }),
    );
  }

  getWorldRankSettings(): Observable<WorldRankSettings> {
    const url = `${this.baseUrl}event/v1/get_world_rank_settings`;
    return this.http
      .get<{ data: WorldRankSettings }>(url)
      .pipe(map((response) => response.data));
  }

  getCurrentSystemSettings(): LeaderboardSystemSettings {
    return this.systemSettingsSubject.value;
  }

  getCurrentLeaderboardMode(): LeaderboardMode {
    return this.leaderboardModeSubject.value;
  }

  getSystemSettings(): Observable<LeaderboardSystemSettings> {
    const url = `${this.baseUrl}ott/v1/tenant/${environment.tenant_Name}/system-settings`;
    return this.http.get<any>(url, this.getHttpOptions()).pipe(
      map((response) => this.normalizeSystemSettings(response)),
      tap((settings) => {
        this.systemSettingsSubject.next(settings);

        this.leaderboardModeSubject.next(this.resolveLeaderboardMode(settings));
      }),
      catchError((error) => {
        console.error('Error fetching system settings:', error);
        return of(this.systemSettingsSubject.value);
      }),
    );
  }

  private normalizeSystemSettings(
    response: any,
  ): LeaderboardSystemSettings {
    const source =
      response && typeof response === 'object' && 'data' in response
        ? response.data
        : response;

    return {
      use_ranking: !!source?.use_ranking,
      use_rating: !!source?.use_rating,
      use_ranking_and_rating: !!source?.use_ranking_and_rating,
    };
  }

  private resolveLeaderboardMode(
    settings: LeaderboardSystemSettings,
  ): LeaderboardMode {
    if (settings.use_ranking_and_rating) {
      return 'RankingAndRating';
    }
    if (settings.use_ranking) {
      return 'Ranking';
    }
    if (settings.use_rating) {
      return 'Rating';
    }
    return 'None';
  }

  getRatingEvents(): Observable<any> {
    const url = `${this.baseUrl}v1/ratings/config/rating/tenant/events/linked`;
    let params = new HttpParams().set('page', 1).set('size', 100);

    // const httpOptions = {
    //   headers: new HttpHeaders({
    //     'Content-Type': 'application/json',
    //     'ngrok-skip-browser-warning': 'true',
    //     // Add auth token if required by your application setup
    //   }),
    // };
    return this.http.get<any>(url, this.getHttpOptions(params)).pipe(
      map((response) => {
        if (response) {
          return response;
        }
        return [];
      }),
      catchError((err) => {
        console.error('Error fetching events:', err);
        return throwError(() => new Error('Failed to fetch events'));
      }),
    );
  }

  getRankings(
    seasonId: number | null | undefined,
    categoryId: number,
    page: number = 1,
    perPage: number = 10,
    parents: number[] = [],
    filterRankMin?: number,
    filterRankMax?: number,
    dateFrom?: string,
    dateTo?: string,
    search?: string,
    sortBy: string = 'rank',
    sortOrder: string = 'asc',
    filterEventIds: number[] = [],
  ): Observable<RankingsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString())
      .set('sort_by', sortBy)
      .set('sort_order', sortOrder);
    if (parents.length > 0) {
      params = params.set('parents', parents.join(','));
    }
    if (filterRankMin !== undefined) {
      params = params.set('filter_rank_min', filterRankMin.toString());
    }
    if (filterRankMax !== undefined) {
      params = params.set('filter_rank_max', filterRankMax.toString());
    }
    if (search) {
      params = params.set('search', search);
    }
    if (seasonId) {
      params = params.set('season_id', seasonId.toString());
    }
    if (categoryId) {
      params = params.set('category_ids', categoryId.toString());
    }
    if (dateFrom) {
      params = params.set('date_from', dateFrom);
    }
    if (dateTo) {
      params = params.set('date_to', dateTo);
    }
    if (filterEventIds.length > 0) {
      params = params.set('event_ids', filterEventIds.join(','));
    }
    return this.http
      .get<RankingsResponse>(
        rnr_end_points.ranking_standing_replica,
        this.getHttpOptions(params),
      )
      .pipe(
        catchError((error) => {
          console.error('Error fetching rankings:', error);
          return of({
            items: [],
            standings: [],
            metadata: {
              calculation_timestamp: '',
              filters_applied: {
                season_id: seasonId ?? 0,
                category_id: categoryId,
                urs_data_for_entity_map_json_str: [],
              },
              rnr_tenant_context: '',
              total_count: 0,
              current_page: 1,
              per_page: perPage,
              total_pages: 1,
            },
          });
        }),
      );
  }

  exportRankings(payload: ExportRankingsPayload): Observable<Blob> {
    const url = `${this.baseUrl}v1/rankings/ranking/standings/export`;
    let params = new HttpParams()
      .set('season_id', payload.season_id.toString())
      .set('category_id', payload.category_id.toString())
      .set('format', payload.format);

    if (payload.date_from) {
      params = params.set('date_from', payload.date_from);
    }
    if (payload.date_to) {
      params = params.set('date_to', payload.date_to);
    }
    if (payload.event_ids && payload.event_ids.length > 0) {
      payload.event_ids.forEach((eventId) => {
        params = params.append('event_ids', eventId.toString());
      });
    }
    if (payload.parents && payload.parents.length > 0) {
      params = params.set('parents', payload.parents.join(','));
    }
    if (payload.filter_rank_min !== undefined) {
      params = params.set(
        'filter_rank_min',
        payload.filter_rank_min.toString(),
      );
    }
    if (payload.filter_rank_max !== undefined) {
      params = params.set(
        'filter_rank_max',
        payload.filter_rank_max.toString(),
      );
    }
    if (payload.search) {
      params = params.set('search', payload.search);
    }

    const headers = new HttpHeaders({
      accept: 'application/json',
      tenant: environment.tenant_Name,
      auth:
        'Bearer ' +
        (typeof window !== 'undefined' && localStorage.getItem('token')
          ? localStorage.getItem('token')
          : ''),
      language: 'en',
      'ngrok-skip-browser-warning': 'true',
    });

    return this.http
      .post(url, {}, { headers, params, responseType: 'blob' })
      .pipe(
        catchError((error) => {
          console.error('Error exporting rankings:', error);
          return of(new Blob());
        }),
      );
  }

  getUsers(params: GetUsersParams): Observable<UsersPageResult> {
    const url = `${this.baseUrl}ott/v1/get_all_users`;
    const pageNum = params.pageNum ?? 1;
    const perPage = params.perPage ?? CLUBS_PAGE_SIZE;
    const httpParams = buildHttpParams({
      role_ids: params.roleId,
      tenant: environment.tenant_Name,
      language: 'en',
      page_num: pageNum,
      per_page: perPage,
      search_value: params.searchValue,
    });
    const httpOptions = this.getHttpOptions(httpParams);
    return this.http
      .get<UsersResponse>(url, {
        ...httpOptions,
        ...(params.skipGlobalLoader && {
          context: new HttpContext().set(SKIP_GLOBAL_LOADER, true),
        }),
      })
      .pipe(
        map((response) => {
          if (response.code === 200) {
            return {
              users: response.data ?? [],
              count: response.count ?? response.data?.length ?? 0,
            };
          }
          return { users: [], count: 0 };
        }),
        catchError((error) => {
          console.error('Error fetching users:', error);
          return of({ users: [], count: 0 });
        }),
      );
  }

  getAllRoles(): Observable<any> {
    const url = `${this.baseUrl}ott/v1/get_roles`;
    return this.http.get<HttpRes>(url, this.getHttpOptions()).pipe(
      shareReplay(),
      map((response: any) => {
        return {
          code: response.status,
          msg: response,
          api_response: response.body,
        } as any;
      }),
    );
  }

  getSeasons(limit: number = 1000): Observable<SeasonsResponse> {
    const url = `${this.baseUrl}ott/v1/list_seasons`;
    const params = new HttpParams().set('limit', limit.toString());
    return this.http
      .get<SeasonsResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching seasons:', error);
          return of({
            seasons: [],
            pagination: {
              current_page: 1,
              total_pages: 1,
              total_items: 0,
              limit,
            },
          });
        }),
      );
  }

  getRankingTimeline(
    seasonId: number,
    categoryId: number,
  ): Observable<TimelineResponse> {
    const url = `${this.baseUrl}ott/v1/rankings/timeline`;
    const params = new HttpParams()
      .set('season_id', seasonId.toString())
      .set('category_id', categoryId.toString());
    return this.http
      .get<TimelineResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching ranking timeline:', error);
          return of({ configure: '', by_date: [], by_week: [], by_month: [] });
        }),
      );
  }

  getEventPerformance(
    participantId: number,
    seasonId: number,
    categoryId: number,
  ): Observable<EventPerformanceResponse> {
    const url = `${this.baseUrl}ott/v1/rankings/participants/${participantId}/event-performance/from-snapshot`;
    let params = new HttpParams().set('category_id', categoryId.toString());
    if (seasonId != null) {
      params = params.set('season_id', seasonId.toString());
    }
    return this.http
      .get<EventPerformanceResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching event performance:', error);
          return of({
            participant_id: participantId,
            participant_name: '',
            is_paused: false,
            season_id: seasonId,
            category_id: categoryId,
            event_performances: [],
          });
        }),
      );
  }

  getCombinedEventBreakdown(
    playerId: number,
    categoryId: number,
  ): Observable<CombinedEventBreakdownResponse> {
    const url = `${this.baseUrl}ott/v1/combined-event-breakdown/current/${playerId}`;
    const params = new HttpParams().set('category_id', String(categoryId));
    return this.http
      .get<CombinedEventBreakdownResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching combined event breakdown:', error);
          return of({
            player_id: playerId,
            player_name: null,
            overall_rating_initial: null,
            overall_rating_current: null,
            overall_rating_change: null,
            overall_ranking_points: null,
            overall_total_points: null,
            events: [],
            metadata: undefined,
          });
        }),
      );
  }

  getEventMatchBreakdown(
    playerId: number,
    eventId: number,
    categoryId: number,
  ): Observable<EventMatchBreakdownResponse> {
    const url = `${this.baseUrl}ott/v1/combined-event-breakdown/current/${playerId}/event/${eventId}/matches`;
    const params = new HttpParams().set('category_id', String(categoryId));
    return this.http
      .get<EventMatchBreakdownResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching event match breakdown:', error);
          return of({
            player_id: playerId,
            player_name: null,
            event_id: eventId,
            event_name: '',
            event_date: null,
            category_id: categoryId,
            category_name: '',
            rating_initial: null,
            rating_final: null,
            rating_change: null,
            bonus_points: null,
            total_points: null,
            matches: [],
            total_matches: null,
            wins: null,
            losses: null,
            ties: null,
            metadata: undefined,
          });
        }),
      );
  }

  getExpiredPoints(
    participantId: number,
    seasonId: number,
    categoryId: number,
  ): Observable<ExpiredPointsResponse> {
    const url = `${this.baseUrl}ott/v1/rankings/participants/${participantId}/expired-points`;
    const params = new HttpParams()
      .set('season_id', seasonId.toString())
      .set('category_id', categoryId.toString());
    return this.http
      .get<ExpiredPointsResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching expired points:', error);
          return of({
            participant_id: participantId,
            participant_name: '',
            expired_events: [],
          });
        }),
      );
  }

  getRankingLists(
    seasonId: number,
    categoryId: number,
    eventId: number,
  ): Observable<RankingListsResponse> {
    const url = `${this.baseUrl}ott/v1/rankings/lists`;
    const params = new HttpParams()
      .set('season_id', seasonId.toString())
      .set('category_id', categoryId.toString())
      .set('event_id', eventId.toString());
    return this.http
      .get<RankingListsResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching ranking lists:', error);
          return of({
            items: [],
            total_count: 0,
            current_page: 1,
            per_page: 50,
            total_pages: 1,
          });
        }),
      );
  }

  /**
   * Resolves ranking list id for a category within a specific event (no season filter).
   * @param categoryId - Category identifier from category_breakdown.
   * @param eventId - Tournament/event identifier.
   */
  getRankingListsByCategoryAndEvent(
    categoryId: number,
    eventId: number,
  ): Observable<RankingListsResponse> {
    const url = `${this.baseUrl}v1/rankings/ranking/lists`;
    const params = new HttpParams()
      .set('category_id', categoryId.toString())
      .set('event_id', eventId.toString());
    return this.http
      .get<RankingListsResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching ranking lists by category and event:', error);
          return of({
            items: [],
            total_count: 0,
            current_page: 1,
            per_page: 50,
            total_pages: 1,
          });
        }),
      );
  }

  getComponentSummaryByEventStage(
    rankingListId: string,
    participantId: number,
    eventId: number,
  ): Observable<ComponentSummaryResponse> {
    const url = `${this.baseUrl}ott/v1/rankings/lists/${rankingListId}/participants/${participantId}/component-summary-by-event-stage_v1`;
    const params = new HttpParams().set('event_id', eventId.toString());
    return this.http
      .get<ComponentSummaryResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching component summary:', error);
          return of({
            ranking_list_id: rankingListId,
            participant_id: participantId,
            participant_name: '',
            participant_type_id: 0,
            participant_user_role_ids: '',
            events_breakdown: [],
            overall_total_points_in_list: 0,
          });
        }),
      );
  }

  /**
   * Fetches v2 component summary for a participant within a ranking list and event.
   * @param rankingListId - Ranking list identifier.
   * @param participantId - Participant identifier.
   * @param eventId - Tournament/event identifier.
   */
  getComponentSummaryByEventStageV2(
    rankingListId: string,
    participantId: number,
    eventId: number,
  ): Observable<ComponentSummaryV2Response> {
    const url = `${this.baseUrl}ott/v1/rankings/lists/${rankingListId}/participants/${participantId}/component-summary-by-event-stage`;
    const params = new HttpParams().set('event_id', eventId.toString());
    return this.http
      .get<ComponentSummaryV2Response>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching v2 component summary:', error);
          return throwError(() => error);
        }),
      );
  }

  togglePause(
    participantId: number,
    startDate: string,
    reason: string,
    userId: string,
  ): Observable<any> {
    const url = `${this.baseUrl}v1/rankings/ranking/participants/${participantId}/pause`;
    const body = {
      start_date: startDate,
      reason: reason,
      user_id: userId,
    };
    return this.http.post(url, body, this.getHttpOptions()).pipe(
      catchError((error) => {
        console.error('Error toggling pause status:', error);
        return of({});
      }),
    );
  }

  getOngoingSeason(queryDate: string): Observable<SeasonsResponse> {
    const url = `${this.baseUrl}ott/v1/list_seasons`;
    const params = new HttpParams().set('query_date', queryDate);
    return this.http
      .get<SeasonsResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching ongoing season:', error);
          return of({
            seasons: [],
            pagination: {
              current_page: 1,
              total_pages: 1,
              total_items: 0,
              limit: 100,
            },
          });
        }),
      );
  }
  /**
   * Fetches per-category rating breakdown for a player within a specific tournament.
   * @param playerId - Rating player identifier
   * @param eventId - Tournament/event identifier
   * @returns Observable of tournament-level category breakdown data
   */
  getRatingPlayerTournamentBreakdown(
    playerId: number,
    eventId: number,
  ): Observable<RatingTournamentBreakdownResponse> {
    const url = `${this.baseUrl}ott/v1/rating/player/${playerId}/breakdown/tournament/${eventId}`;
    const params = new HttpParams().set('source', 'leaderboard');

    return this.http
      .get<RatingTournamentBreakdownResponse>(url, this.getHttpOptions(params))
      .pipe(
      catchError((error) => {
        console.error('Error fetching rating tournament breakdown:', error);
        return throwError(
          () => new Error('Failed to fetch rating tournament breakdown'),
        );
      }),
    );
  }

  /**
   * Fetches match-level rating breakdown for a player within a tournament category.
   * @param playerId - Rating player identifier
   * @param eventId - Tournament/event identifier
   * @param categoryId - Category identifier
   */
  getRatingPlayerMatchLevelBreakdown(
    playerId: number,
    eventId: number,
    categoryId: number,
  ): Observable<RatingMatchLevelBreakdownResponse> {
    const url = `${this.baseUrl}ott/v1/rating/player/${playerId}/breakdown/tournament/${eventId}/category/${categoryId}/match-level`;
    const params = new HttpParams().set('source', 'leaderboard');

    return this.http
      .get<RatingMatchLevelBreakdownResponse>(url, this.getHttpOptions(params))
      .pipe(
        catchError((error) => {
          console.error('Error fetching rating match-level breakdown:', error);
          return throwError(
            () => new Error('Failed to fetch rating match-level breakdown'),
          );
        }),
      );
  }

  getRatingBreakdown(playerId: number, as_of_date: string): Observable<any> {
    const url = `${this.baseUrl}ott/v1/rating/player/${playerId}/leaderboard-breakdown`;

    const params = new HttpParams()
      .set('tenant_id', environment.tenant_Name)
      .set('as_of_date', as_of_date);

    // Use a simplified httpOptions since this is a GET request with params
    const httpOptions = {
      headers: new HttpHeaders({
        tenant: environment.tenant_Name,
        'ngrok-skip-browser-warning': 'true',
        auth:
          'Bearer ' +
          (typeof window !== 'undefined' && localStorage.getItem('token')
            ? localStorage.getItem('token')
            : ''),
      }),
      params: params,
    };

    return this.http.get<any>(url, httpOptions).pipe(
      catchError((error) => {
        console.error('Error fetching rating breakdown:', error);
        return throwError(() => new Error('Failed to fetch rating breakdown'));
      }),
    );
  }

  getRatingSettings(): Observable<any> {
    const apiURL = `${environment.apiUrl}v1/ratings/config/rating/rating-settings`;
    const token = localStorage.getItem('token') || '';
    return this.http
      .get(apiURL, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          auth: `Bearer ${token}`,
          tenant: environment.tenant_Name,
          language: 'en',
          'ngrok-skip-browser-warning': 'true',
        }),
      })
      .pipe(
        catchError((error) => {
          const sb = this.snackBar.open(
            'Failed to fetch rating settings. Please try again.',
            'Close',
            { duration: 3000, panelClass: ['error'] },
          );
          sb.onAction().subscribe(() => sb.dismiss());
          return throwError(() => new Error('Failed to fetch rating settings'));
        }),
      );
  }

  getFilterConfig(): Observable<RatingFilterConfig> {
    const apiUrl = `${environment.apiUrl}v1/ratings/config/rating/tenant/filters`;
    return this.http
      .get<RatingFilterConfig>(apiUrl, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          accept: 'application/json',
          tenant: environment.tenant_Name,
          language: 'en',
          auth: localStorage.getItem('token') || '',
          'ngrok-skip-browser-warning': 'true',
        }),
      })
      .pipe(catchError((error) => this.handleError('fetch filter config')));
  }

  getLinkedCategoriesForTenant(
    tenantId: string,
    level?: string,
    page?: number,
    size?: number,
  ): Observable<PaginatedCategoriesResponse> {
    const url = `${environment.apiUrl}v1/ratings/config/rating/tenant`;
    let params = new HttpParams();
    if (level) params = params.set('level', level);
    if (page) params = params.set('page', page.toString());
    if (size) params = params.set('size', size.toString());

    return this.http
      .get<PaginatedCategoriesResponse>(`${url}/categories/linked`, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          accept: 'application/json',
          tenant: environment.tenant_Name,
          language: 'en',
          auth: localStorage.getItem('token') || '',
          'ngrok-skip-browser-warning': 'true',
        }),
        ...(params ? { params } : {}),
      })
      .pipe(catchError((error) => this.handleError('fetch linked categories')));
  }

  private handleError(action: string) {
    const sb = this.snackBar.open(
      `Failed to ${action}. Please try again.`,
      'Close',
      {
        duration: 3000,
        panelClass: ['error'],
      },
    );
    sb.onAction().subscribe(() => sb.dismiss());
    return throwError(() => new Error(`Failed to ${action}`));
  }

  getLastPublishedDate(payload: LastPublishedDatePayload): Observable<any> {
    const params = buildHttpParams(payload ?? {});
    return this.http
      .get<HttpRes>(
        rnr_end_points.last_published_date,
        this.getHttpOptions(params),
      )
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            data: response,
          } as HttpRes;
        }),
      );
  }
}

export interface RatingAdjustmentPayload {
  new_rating: number;
  reason_type: 'penalty' | 'bonus' | 'victory_point';
  reason: string;
  admin_user_id: string;
  context_event_id: number;
  context_details: {
    adjustment_type: 'penalty' | 'bonus' | 'victory_point';
    category_id: number;
    match_id?: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class EditSidebarService {
  private baseApiUrl = `${environment.apiUrl}`;
  private apiUrl = `${this.baseApiUrl}ott/v1/rankings/lists/`;
  // private configUrl = `${this.baseApiUrl}config/tenant/ettu/ranking-settings`;

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar, // Added for snackbar
  ) {}

  getComponentSummary(
    rankingListId: string,
    participantId: string,
  ): Observable<ComponentSummaryResponse> {
    const url = `${this.apiUrl}${rankingListId}/participants/${participantId}/component-summary-by-event-stage_v1`;
    // const url = "https://run.mocky.io/v3/e2f6097b-edea-477f-b303-9705bb6b547e";
    const headers = new HttpHeaders({
      tenant_id: environment.tenant_Name,
    });

    return this.http.get<ComponentSummaryResponse>(url, { headers }).pipe(
      catchError((err) => {
        console.error('Error fetching component summary:', err);
        return throwError(() => new Error('Failed to fetch component summary'));
      }),
    );
  }

  // getRankingSettings(): Observable<RankingSettingsResponse> {
  //     const url = this.configUrl;
  //     const headers = new HttpHeaders({
  //       'tenant_id': 'ettu'
  //     });

  //     return this.http.get<RankingSettingsResponse>(url, { headers }).pipe(
  //       catchError(err => {
  //         console.error('Error fetching ranking settings:', err);
  //         return throwError(() => new Error('Failed to fetch ranking settings'));
  //       })
  //     );
  //   }

  adjustTotalPoints(
    rankingListId: string,
    participantId: string,
    payload: any,
  ): Observable<any> {
    const url = `${this.apiUrl}${rankingListId}/participants/${participantId}/adjust-total`;
    const headers = new HttpHeaders({
      tenant_id: environment.tenant_Name,
      'Content-Type': 'application/json',
    });

    return this.http.put(url, payload, { headers }).pipe(
      catchError((err) => {
        console.error('Error adjusting total points:', err);
        return throwError(() => new Error('Failed to adjust total points'));
      }),
    );
  }

  getParameters(): {
    ranking_list_id: string;
    participant_id: string;
    event_id: string;
    category_id: number;
  } {
    // Added category_id
    const rankingListId = localStorage.getItem('Ranking_list_id');
    const participantId = localStorage.getItem('participant_id');
    const rankingManagement = JSON.parse(
      localStorage.getItem('rankingManagement') || '[]',
    );
    const eventId = rankingManagement[0]?.event_id;
    const categoryId = rankingManagement[0]?.category_id; // Get category_id

    // Ranking list ID is now optional, only required for Ranking mode
    const systemType = localStorage.getItem('system-design');
    if (systemType === 'Ranking' && !rankingListId) {
      this.snackBar.open('Ranking list not found', 'Close', {
        duration: 3000,
        panelClass: ['error-snackbar'],
      });
      throw new Error('Ranking list ID is missing for Ranking mode');
    }

    if (!participantId) {
      this.snackBar.open('Participant ID not found', 'Close', {
        duration: 3000,
        panelClass: ['error-snackbar'],
      });
      throw new Error('Participant ID is missing');
    }

    if (!eventId) {
      this.snackBar.open('Event ID not found', 'Close', {
        duration: 3000,
        panelClass: ['error-snackbar'],
      });
      throw new Error('Event ID is missing');
    }

    if (!categoryId) {
      // Add check for categoryId
      this.snackBar.open('Category ID not found', 'Close', {
        duration: 3000,
        panelClass: ['error-snackbar'],
      });
      throw new Error('Category ID is missing');
    }

    return {
      ranking_list_id: rankingListId || '', // Return empty string if not found
      participant_id: participantId,
      event_id: eventId,
      category_id: categoryId, // Return categoryId
    };
  }

  refinalizeRankingList(
    rankingListId: string,
    requestingUserId: string,
  ): Observable<any> {
    const url = `${this.apiUrl}${rankingListId}/refinalize?requesting_user_id=${requestingUserId}`;

    const headers = new HttpHeaders({
      tenant: environment.tenant_Name,
      'Content-Type': 'application/json',
    });

    return this.http.post(url, null, { headers }).pipe(
      catchError((err) => {
        console.error('Error refinalizing ranking list:', err);
        return throwError(() => new Error('Failed to refinalize ranking list'));
      }),
    );
  }

  getRatingTournamentBreakdown(
    playerId: string,
    eventId: string,
    categoryId: number,
  ): Observable<any> {
    const url = `${this.baseApiUrl}v1/ratings/player/${playerId}/breakdown/tournament/${eventId}/category/${categoryId}`;
    const headers = new HttpHeaders({ tenant_id: environment.tenant_Name });
    return this.http.get<any>(url, { headers }).pipe(
      catchError((err) => {
        console.error('Error fetching rating tournament breakdown:', err);
        return throwError(
          () => new Error('Failed to fetch rating tournament breakdown'),
        );
      }),
    );
  }

  getRatingMatchLevelBreakdown(
    playerId: string,
    eventId: string,
    categoryId: number,
  ): Observable<any> {
    const url = `${this.baseApiUrl}v1/ratings/player/${playerId}/breakdown/tournament/${eventId}/category/${categoryId}/match-level`;
    const headers = new HttpHeaders({ tenant_id: environment.tenant_Name });
    return this.http.get<any>(url, { headers }).pipe(
      catchError((err) => {
        console.error('Error fetching rating match-level breakdown:', err);
        return throwError(
          () => new Error('Failed to fetch rating match-level breakdown'),
        );
      }),
    );
  }

  getRatingLists(tenantId: string): Observable<any> {
    const url = `${this.baseApiUrl}v1/ratings/lists`;

    // Header 'tenant' is required by the mock server
    const headers = new HttpHeaders({ tenant: tenantId });

    // Query parameters are built using HttpParams
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('page', '1')
      .set('per_page', '50');

    return this.http.get<any>(url, { headers, params }).pipe(
      catchError((err) => {
        console.error('Error fetching rating lists:', err);
        return throwError(() => new Error('Failed to fetch rating lists'));
      }),
    );
  }

  getLiveRating(playerId: string, tenantId: string): Observable<any> {
    const url = `${this.baseApiUrl}v1/ratings/player/${playerId}/live-rating`;

    // Header 'tenant' is required by the mock server
    const headers = new HttpHeaders({ tenant: tenantId });

    // Query parameter 'tenant_id' is required by the mock server
    const params = new HttpParams().set('tenant_id', tenantId);

    return this.http.get<any>(url, { headers, params }).pipe(
      catchError((err) => {
        console.error('Error fetching live rating:', err);
        return throwError(() => new Error('Failed to fetch live rating'));
      }),
    );
  }

  adjustRatingEntry(
    ratingListId: string,
    playerId: string,
    payload: any,
  ): Observable<any> {
    const url = `${this.baseApiUrl}v1/ratings/lists/${ratingListId}/entries/${playerId}/adjust`;

    const headers = new HttpHeaders({
      tenant: environment.tenant_Name,
      'Content-Type': 'application/json',
    });

    const params = new HttpParams().set('tenant_id', environment.tenant_Name);

    return this.http.put(url, payload, { headers, params }).pipe(
      catchError((err) => {
        console.error('Error adjusting rating entry:', err);
        return throwError(() => new Error('Failed to adjust rating entry'));
      }),
    );
  }

  refinalizeRatingList(ratingListId: string, userId: string): Observable<any> {
    const url = `${this.baseApiUrl}v1/ratings/lists/${ratingListId}/refinalize`;

    // As per CURL, 'tenant' is a header. Using 'ettu' for consistency.
    const headers = new HttpHeaders({
      tenant: environment.tenant_Name,
      'Content-Type': 'application/json',
    });

    // The user_id is a query parameter
    const params = new HttpParams().set('user_id', userId);

    // This is a POST request with a null body
    return this.http.post(url, null, { headers, params }).pipe(
      catchError((err) => {
        console.error('Error refinalizing rating list:', err);
        return throwError(() => new Error('Failed to refinalize rating list'));
      }),
    );
  }

  // Add this new method to your EditSidebarService class

  getLeaderboardBreakdown(
    playerId: string,
    tenantId: string,
    ratingListId?: string | null,
    asOfDate?: string | null,
    seasonId?: number | null,
    categoryId?: number | null,
    tournamentIds?: number[] | null,
  ): Observable<any> {
    const url = `http://127.0.0.1:8000/v1/ratings/player/${playerId}/leaderboard-breakdown`;
    const headers = new HttpHeaders({ tenant: tenantId });
    let params = new HttpParams().set('tenant_id', tenantId);

    if (ratingListId) {
      params = params.set('rating_list_id', ratingListId);
    }
    if (asOfDate) {
      params = params.set('as_of_date', asOfDate);
    }
    if (seasonId) {
      params = params.set('season_id', seasonId.toString());
    }
    if (categoryId) {
      params = params.set('category_id', categoryId.toString());
    }
    if (tournamentIds && tournamentIds.length > 0) {
      tournamentIds.forEach((id) => {
        params = params.append('tournament_ids', id.toString());
      });
    }

    return this.http.get<any>(url, { headers, params }).pipe(
      catchError((err) => {
        console.error('Error fetching leaderboard breakdown:', err);
        return throwError(
          () => new Error('Failed to fetch leaderboard breakdown'),
        );
      }),
    );
  }

  // BULK FOR THE COMPONENT SUMMARY

  getBulkComponnentSummary(
    payload: Partial<BulkComponentSummaryPayload>,
  ): Observable<HttpRes> {
    return this.http
      .post<HttpRes>(rating_urls.bulk_component_summary, payload, {
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            data: response.body,
          } as HttpRes;
        }),
      );
  }




}


